/**
 * The one definition of "which build am I?".
 *
 * Every surface that shows or reports a version reads it from here — the
 * settings screens, the contact form, and the Faro release tag in _layout.tsx.
 * Before this existed the contact screen carried a hardcoded '1.0.0' that was
 * displayed to users AND submitted on every support ticket, so the one column
 * that would let you diff a release against a complaint was dead.
 *
 * The two platforms resolve it from genuinely different places, and neither can
 * be swapped for the other:
 *
 *   Web    — the backend serves the bundle, so "which build" is whatever that
 *            backend is currently serving. It arrives at runtime on
 *            window.__CYNOSURE_CONFIG__ (injected into index.html at startup by
 *            backend/app/main.py) and is the chart appVersion, e.g. "0.1.41".
 *            NOT a build arg: one image is deployed to every cluster.
 *
 *   Native — the binary came from the App Store, not from us, so the version is
 *            whatever was frozen into it at build time. `version` is the
 *            marketing string from app.json; expo-application's
 *            `nativeBuildVersion` is the
 *            CFBundleVersion / versionCode that EAS auto-increments per
 *            production build (appVersionSource: "remote" in eas.json, so it
 *            lives on EAS servers and never appears in this repo).
 *
 * Both are included on native because `version` alone does not move per
 * release — a normal ship goes 1.0.0 (124) -> 1.0.0 (125) — and a release tag
 * that cannot distinguish two builds makes "did the last deploy break it"
 * unanswerable.
 *
 * The fallback is 'unknown', deliberately never a plausible-looking version: a
 * missing value should read as missing rather than quietly masquerade as a real
 * build, which is exactly the failure this module exists to undo.
 *
 * The build number must come from expo-application. expo-constants dropped its
 * own `nativeBuildVersion` (SDK 55 has no such property), and reading it there
 * returns undefined without a type error (the Constants type admits any key) —
 * every native build up to 1.0.0 (6) reported a bare "1.0.0" as a result.
 */

import * as Application from 'expo-application';
import Constants from 'expo-constants';
import { Platform } from 'react-native';

/** Key into the /api/v1/meta response — see useVersionStatus. */
export type PlatformKey = 'web' | 'ios' | 'android';

export const UNKNOWN_VERSION = 'unknown';

interface RuntimeConfig {
  release?: string;
}

function runtimeRelease(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as unknown as { __CYNOSURE_CONFIG__?: RuntimeConfig }).__CYNOSURE_CONFIG__
    ?.release;
}

/**
 * The running build's version string, in the same format the backend publishes
 * for this platform: "0.1.41" on web, "1.0.0+125" on native.
 */
export function currentVersion(): string {
  if (Platform.OS === 'web') return runtimeRelease() ?? UNKNOWN_VERSION;

  const version = Constants.expoConfig?.version;
  if (!version) return UNKNOWN_VERSION;

  const build = Application.nativeBuildVersion;
  return build ? `${version}+${build}` : version;
}

/** Which /api/v1/meta field describes this client. */
export function platformKey(): PlatformKey {
  if (Platform.OS === 'ios') return 'ios';
  if (Platform.OS === 'android') return 'android';
  return 'web';
}

/** Human-facing platform name for the settings screens. */
export function platformLabel(): string {
  if (Platform.OS === 'ios') return 'iOS';
  if (Platform.OS === 'android') return 'Android';
  return 'Web';
}

/**
 * Accepts the two shapes this app produces — "0.1.41" on web and "1.0.0+125" on
 * native — and nothing else. Anything unparseable (including 'unknown' and the
 * backend's "dev" default) is rejected rather than coerced, because a bad parse
 * here shows a user a confident and wrong claim about their build.
 */
const VERSION_RE = /^\d+(?:[.+]\d+)*$/;

function versionParts(version: string): number[] | null {
  if (!VERSION_RE.test(version)) return null;
  return version.split(/[.+]/).map(Number);
}

/**
 * Orders two version strings: negative if `a` is older, 0 if equal, positive if
 * `a` is newer. Returns null when either side cannot be parsed — the caller must
 * treat that as "no opinion", never as out of date.
 *
 * The build number is compared as one more trailing segment, so "1.0.0" sorts
 * below "1.0.0+125". That is the correct reading: a build-less string is what
 * you get before EAS has stamped one.
 *
 * Only ever compares like with like. Web and native version strings live in
 * unrelated sequences ("0.1.41" is not older than "1.0.0"), which is why
 * /api/v1/meta reports them as separate fields.
 */
export function compareVersions(a: string, b: string): number | null {
  const pa = versionParts(a);
  const pb = versionParts(b);
  if (!pa || !pb) return null;

  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}
